// models/auth.ts

export interface CodeDeliveryDetails {
  destination: string;
  deliveryMedium: string;
  attributeName: string;
}

export interface SignupRequest {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  jobTitle?: string;
}

export interface SignupResponse {
  userSub: string;
  companyId: string;
  codeDeliveryDetails: CodeDeliveryDetails;
  timestamp: string;
}

export interface SigninRequest {
  email: string;
  password: string;
}

export interface SigninResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
  isNewUser?: boolean;
  user: {
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    companyId: string;
  };
}

export interface ConfirmSignupRequest {
  email: string;
  confirmationCode: string;
}

export interface ConfirmSignupResponse {
  accessToken: string;
  idToken: string;
  refreshToken: string;
  expiresIn: number;
  tokenType: string;
  user: {
    userId: string;
    email: string;
    firstName: string;
    lastName: string;
    companyId: string;
  };
}

export interface ResendConfirmationRequest {
  email: string;
}

export interface ResendConfirmationResponse {
  codeDeliveryDetails: CodeDeliveryDetails;
  timestamp: string;
}

export interface CurrentUserResponse {
  userId: string;
  email: string;
  emailVerified: boolean;
  firstName: string;
  lastName: string;
  companyId: string;
  jobTitle: string;
  defaultPipeline?: string;
  /** Linked HubSpot owner id. Absent when unlinked or declined (REV-204). */
  hubspotOwnerId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpdateProfileRequest {
  firstName?: string;
  lastName?: string;
  jobTitle?: string;
  defaultPipeline?: string;
  /**
   * HubSpot owner id to link to, or {@link OWNER_LINK_DECLINED} to record that
   * the user declined. The server validates an owner id against the tenant's
   * non-archived owners and rejects anything else (REV-204).
   */
  hubspotOwnerId?: string;
}

/**
 * Reserved value meaning "offered a link and declined it" (REV-204).
 *
 * Clearing the attribute outright is indistinguishable from never having set
 * it, so a cleared link would be re-suggested on the next load.
 */
export const OWNER_LINK_DECLINED = "none";

/**
 * Whether the signed-in user is linked to a HubSpot owner (REV-204).
 *
 * `unlinked` is a normal state, not an error: it covers no match, several
 * matches (where the server deliberately refuses to auto-pick), a declined
 * link, and a link whose owner has since been archived.
 */
export interface HubspotOwnerLink {
  status: "linked" | "suggested" | "unlinked";
  ownerId?: string;
  ownerName?: string;
  ownerEmail?: string;
}

export interface UpdateProfileResponse {
  message: string;
  user: CurrentUserResponse;
}

export interface ChangePasswordRequest {
  currentPassword: string;
  newPassword: string;
}

export interface ChangePasswordResponse {
  message: string;
  timestamp: string;
}